import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Storage from '../utils/storageES.js'
import HomeButton from './HomeButton.jsx'
import PostcardTeaser from './PostcardTeaser.jsx'

function Gifts({ userData, onNavigate, embedded = false }) {
  const { t } = useTranslation()
  const [giftList, setGiftList] = useState([])
  const [isAddingGift, setIsAddingGift] = useState(false)
  const [editingGift, setEditingGift] = useState(null)
  const [formData, setFormData] = useState({
    person: '',
    gift: '',
    reason: '',
    purchased: false
  })

  useEffect(() => {
    loadGifts()
  }, [])

  const loadGifts = () => {
    const tripData = Storage.getTripData()
    const savedGifts = tripData.gifts || []
    setGiftList(savedGifts)
  }

  const saveGifts = (gifts) => {
    const tripData = Storage.getTripData()
    tripData.gifts = gifts
    Storage.saveTripData(tripData)
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const addOrUpdateGift = () => {
    if (!formData.person.trim() || !formData.gift.trim()) {
      alert(t('gifts.fillPersonGift'))
      return
    }

    const giftData = {
      id: editingGift ? editingGift.id : Date.now(),
      person: formData.person.trim(),
      gift: formData.gift.trim(),
      reason: formData.reason.trim(),
      purchased: formData.purchased,
      dateAdded: editingGift ? editingGift.dateAdded : new Date().toISOString()
    }

    let updatedGifts
    if (editingGift) {
      // Update existing gift
      updatedGifts = giftList.map(gift => 
        gift.id === editingGift.id ? giftData : gift
      )
    } else {
      // Add new gift
      updatedGifts = [...giftList, giftData]
    }

    setGiftList(updatedGifts)
    saveGifts(updatedGifts)
    resetForm()
  }

  const editGift = (gift) => {
    setEditingGift(gift)
    setFormData({
      person: gift.person,
      gift: gift.gift,
      reason: gift.reason,
      purchased: gift.purchased
    })
    setIsAddingGift(true)
  }

  const deleteGift = (id) => {
    if (confirm(t('gifts.confirmDelete'))) {
      const updatedGifts = giftList.filter(gift => gift.id !== id)
      setGiftList(updatedGifts)
      saveGifts(updatedGifts)
    }
  }

  const togglePurchased = (id) => {
    const updatedGifts = giftList.map(gift =>
      gift.id === id ? { ...gift, purchased: !gift.purchased } : gift
    )
    setGiftList(updatedGifts)
    saveGifts(updatedGifts)
  }

  const resetForm = () => {
    setFormData({
      person: '',
      gift: '',
      reason: '',
      purchased: false
    })
    setIsAddingGift(false)
    setEditingGift(null)
  }

  const getStats = () => {
    const total = giftList.length
    const purchased = giftList.filter(gift => gift.purchased).length
    const remaining = total - purchased
    return { total, purchased, remaining }
  }

  const stats = getStats()

  // Group gifts by person
  const giftsByPerson = giftList.reduce((acc, gift) => {
    if (!acc[gift.person]) {
      acc[gift.person] = []
    }
    acc[gift.person].push(gift)
    return acc
  }, {})

  const content = (
    <>
      {/* Header */}
      {!embedded && <div style={{
        textAlign: 'center',
        marginBottom: '30px',
        paddingTop: '60px'
      }}>
        <h1 style={{
          fontSize: '36px',
          fontWeight: '900',
          color: '#8B7355',
          margin: '0 0 8px 0',
          letterSpacing: '2px',
          fontFamily: "'Noto Serif', serif",
          textTransform: 'uppercase'
        }}>{t('gifts.title')}</h1>
        <p style={{
          fontSize: '14px',
          color: '#8B7355',
          margin: '0 0 16px 0',
          opacity: '0.7'
        }}>{t('gifts.giftsFrom', { city: userData.destination?.toUpperCase() || t('notes.yourJourney') })}</p>
        
        {/* Stats */}
        <div style={{
          background: 'white',
          borderRadius: '25px',
          padding: '16px 20px',
          margin: '0 auto 20px auto',
          maxWidth: '350px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#B8A082' }}>{stats.total}</div>
              <div style={{ fontSize: '12px', color: '#8B7355', opacity: '0.8' }}>{t('gifts.tabIdeas')}</div>
            </div>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#D4A574' }}>{stats.purchased}</div>
              <div style={{ fontSize: '12px', color: '#8B7355', opacity: '0.8' }}>{t('gifts.tabBought')}</div>
            </div>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#8B7355' }}>{stats.remaining}</div>
              <div style={{ fontSize: '12px', color: '#8B7355', opacity: '0.8' }}>{t('gifts.tabToBuy')}</div>
            </div>
          </div>
        </div>
      </div>}

      {/* Sprint 4B — the trip city's postcard as a keepsake/gift (sober mention) */}
      {userData?.destination && (
        <div style={{ margin: '0 0 16px' }}>
          <PostcardTeaser country={userData.country} city={userData.destination} />
        </div>
      )}

      {/* Add/Edit Gift Form */}
      {isAddingGift ? (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          margin: '0 0 20px 0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#B8A082',
            margin: '0 0 20px 0'
          }}>
            {editingGift ? t('gifts.editGiftIdea') : t('gifts.addGiftIdeaTitle')}
          </h3>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#8B7355',
              marginBottom: '6px'
            }}>
              {t('gifts.forWhom')}
            </label>
            <input
              type="text"
              value={formData.person}
              onChange={(e) => handleInputChange('person', e.target.value)}
              placeholder={t('gifts.placeholderPerson')}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #ede4d3',
                borderRadius: '12px',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#8B7355',
              marginBottom: '6px'
            }}>
              {t('gifts.giftIdea')}
            </label>
            <input
              type="text"
              value={formData.gift}
              onChange={(e) => handleInputChange('gift', e.target.value)}
              placeholder={t('gifts.placeholderGift')}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #ede4d3',
                borderRadius: '12px',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#8B7355',
              marginBottom: '6px'
            }}>
              {t('gifts.whyThisGift')}
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => handleInputChange('reason', e.target.value)}
              placeholder={t('gifts.placeholderReason')}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #ede4d3',
                borderRadius: '12px',
                fontSize: '14px',
                outline: 'none',
                minHeight: '80px',
                resize: 'vertical',
                boxSizing: 'border-box',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {editingGift && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '14px',
                fontWeight: '600',
                color: '#8B7355',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={formData.purchased}
                  onChange={(e) => handleInputChange('purchased', e.target.checked)}
                  style={{
                    marginRight: '8px',
                    accentColor: '#C4714A'
                  }}
                />
                {t('gifts.alreadyPurchased')}
              </label>
            </div>
          )}

          <div style={{
            display: 'flex',
            gap: '12px'
          }}>
            <button
              onClick={addOrUpdateGift}
              style={{
                background: '#C4714A',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '25px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                flex: '1'
              }}
            >
              {editingGift ? t('gifts.updateGift') : t('gifts.addGift')}
            </button>
            <button
              onClick={resetForm}
              style={{
                background: '#ccc',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '25px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                flex: '1'
              }}
            >
              {t('buttons.cancel')}
            </button>
          </div>
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          marginBottom: '20px'
        }}>
          <button
            onClick={() => setIsAddingGift(true)}
            style={{
              background: '#C4714A',
              color: 'white',
              border: 'none',
              padding: '16px 32px',
              borderRadius: '30px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            {t('gifts.addNewGiftIdea')}
          </button>
        </div>
      )}

      {/* Gift List */}
      {giftList.length === 0 ? (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '40px 20px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎁</div>
          <h3 style={{
            fontSize: '18px',
            color: '#8B7355',
            margin: '0 0 8px 0'
          }}>{t('gifts.noGifts')}</h3>
          <p style={{
            fontSize: '14px',
            color: '#999',
            margin: '0'
          }}>{t('gifts.startAdding')}</p>
        </div>
      ) : (
        <>
          {Object.keys(giftsByPerson).length > 1 ? (
            // Grouped by person when multiple people
            Object.entries(giftsByPerson).map(([person, gifts]) => (
              <div key={person} style={{
                background: 'white',
                borderRadius: '16px',
                padding: '20px',
                margin: '0 0 16px 0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#B8A082',
                  margin: '0 0 16px 0',
                  borderBottom: '2px solid #ede4d3',
                  paddingBottom: '8px'
                }}>
                  🎁 {t('gifts.for')} {person}
                </h3>
                
                {gifts.map(gift => (
                  <div key={gift.id} style={{
                    padding: '16px 0',
                    borderBottom: gifts[gifts.length - 1] === gift ? 'none' : '1px solid #f5f5f5'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px'
                    }}>
                      <div style={{ flex: '1' }}>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: gift.purchased ? '#999' : '#333',
                          textDecoration: gift.purchased ? 'line-through' : 'none',
                          marginBottom: '4px'
                        }}>
                          {gift.gift}
                          {gift.purchased && <span style={{ marginLeft: '8px', color: '#B8A082' }}>✓</span>}
                        </div>
                        {gift.reason && (
                          <div style={{
                            fontSize: '14px',
                            color: '#666',
                            fontStyle: 'italic',
                            marginBottom: '8px'
                          }}>
                            "{gift.reason}"
                          </div>
                        )}
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        marginLeft: '12px'
                      }}>
                        <button
                          onClick={() => togglePurchased(gift.id)}
                          style={{
                            background: gift.purchased ? '#5B8FA8' : '#ede4d3',
                            color: gift.purchased ? 'white' : '#8B7355',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '15px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          {gift.purchased ? 'Bought' : 'Buy'}
                        </button>
                        <button
                          onClick={() => editGift(gift)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#D4A574',
                            fontSize: '16px',
                            cursor: 'pointer',
                            padding: '4px'
                          }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => deleteGift(gift.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#ccc',
                            fontSize: '16px',
                            cursor: 'pointer',
                            padding: '4px'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          ) : (
            // Simple list when only one person or ungrouped
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              {giftList.map(gift => (
                <div key={gift.id} style={{
                  padding: '16px 0',
                  borderBottom: giftList[giftList.length - 1] === gift ? 'none' : '1px solid #f5f5f5'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px'
                  }}>
                    <div style={{ flex: '1' }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#B8A082',
                        marginBottom: '4px'
                      }}>
                        {t('gifts.for')} {gift.person}
                      </div>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: gift.purchased ? '#999' : '#333',
                        textDecoration: gift.purchased ? 'line-through' : 'none',
                        marginBottom: '4px'
                      }}>
                        {gift.gift}
                        {gift.purchased && <span style={{ marginLeft: '8px', color: '#B8A082' }}>✓</span>}
                      </div>
                      {gift.reason && (
                        <div style={{
                          fontSize: '14px',
                          color: '#666',
                          fontStyle: 'italic'
                        }}>
                          "{gift.reason}"
                        </div>
                      )}
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginLeft: '12px'
                    }}>
                      <button
                        onClick={() => togglePurchased(gift.id)}
                        style={{
                          background: gift.purchased ? '#5B8FA8' : '#ede4d3',
                          color: gift.purchased ? 'white' : '#8B7355',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '15px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        {gift.purchased ? t('gifts.bought') : t('gifts.buy')}
                      </button>
                      <button
                        onClick={() => editGift(gift)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#D4A574',
                          fontSize: '16px',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => deleteGift(gift.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ccc',
                          fontSize: '16px',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Back Button */}
      {!embedded && <div style={{ textAlign: 'center', margin: '30px 0 20px 0' }}>
        <button
          style={{
            background: 'transparent',
            color: '#8B7355',
            border: '2px solid #8B7355',
            padding: '12px 24px',
            borderRadius: '25px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
          onClick={() => onNavigate('home')}
        >
          {t('navigation.backToHome')}
        </button>
      </div>}
    </>
  )

  if (embedded) return content

  return (
    <div className="container gifts-tracker" style={{
      background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
      minHeight: '100vh',
      padding: '20px',
      position: 'relative'
    }}>
      <HomeButton onNavigate={onNavigate} />
      {content}
    </div>
  )
}

export default Gifts